import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { AsyncComponentHookResult } from '../../../../Types/ComponentHookResult';
import { useRouteParams } from '../../hooks/useRouteParams';
import { setSelectedTab } from '../../state/actions';
import { DEFAULT_TAB, GOAL_ICON_MAP } from '../../GoalPage.constants';
import {
  setGoalId,
  setSubGoalType,
} from '../../../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownActions';
import { DebtType, GoalOption, useGoalOptionsQuery } from '../../../api/useGoalOptionsQuery';
import { useFpId } from '../../../hooks/useFpId';
import { getGoalPageName } from '../../utils/getGoalPageName';
import { GoalTypeDTO } from '../../../types/goalsDTO';

export interface Option {
  value: string | number;
  label: string;
  icon?: string;
  isAssetBucketing?: boolean;
  isSummary?: boolean;
  debtType?: DebtType | null;
  goalType?: GoalTypeDTO;
}

interface DropdownData {
  value: Option;
  options: Option[];
}

export const SUMMARY_OPTION: Option = {
  label: 'Summary',
  value: 'summary',
  icon: 'fas fa-list',
  isSummary: true,
};

interface DropdownActions {
  handleOnChange(Option): void;
}

export const useDropdown = (): AsyncComponentHookResult<DropdownData, DropdownActions> => {
  const { goalId, clientId } = useRouteParams();

  const fpId = useFpId();

  const queryResult = useGoalOptionsQuery({ clientId, fpId });

  const dispatch = useDispatch();

  const getIcon = (goalOption: GoalOption) => {
    if (goalOption.goalOrigin === 'PulseCheck') {
      return 'pulseCheck';
    }

    return GOAL_ICON_MAP[goalOption.type];
  };

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const { displayAssetBucketing, assetBucketingName, goals } = queryResult.data;

    const options = goals.reduce(
      (acc: Option[], goalOption: GoalOption) => {
        return [
          ...acc,
          {
            value: goalOption.id,
            label: goalOption.name,
            icon: getIcon(goalOption),
            debtType: goalOption.debtType,
            goalType: goalOption.type,
          },
        ];
      },
      [SUMMARY_OPTION],
    );

    const value = options.find(({ value: _value }) => _value === goalId) as Option;

    if (displayAssetBucketing && assetBucketingName) {
      options.unshift({
        label: assetBucketingName,
        value: 'assetBucketing',
        isAssetBucketing: true,
        icon: 'far fa-envelope-open-dollar',
      });
    }

    return {
      value,
      options: options.filter(goal => goal.value !== goalId),
    };
  }, [queryResult.data, goalId]);

  const handleOnChange = (option: Option | null) => {
    if (option) {
      // When changing a goal, we need to make sure we select the default tab
      dispatch(setSelectedTab({ tab: DEFAULT_TAB }));

      if (option.isAssetBucketing) {
        ADV.util.Router.go2('AssetBucketingResults', { clientId, year: new Date().getFullYear() });
      } else if (option.isSummary) {
        ADV.util.Router.go2('GoalsSummary', {
          clientId,
        });
      } else {
        const goalPageName = option.goalType
          ? getGoalPageName(option.goalType)
          : 'NewFinancialPlanWhatIfs';

        ADV.util.Router.go2(goalPageName, {
          clientId,
          goalId: option.value,
          workflowEnded: false,
        });

        /**
         * TODO (LEGACY): Remove after the migration is complete
         */
        if (option.debtType !== undefined && option.debtType !== null) {
          dispatch(setSubGoalType(option.debtType, clientId));
        } else {
          dispatch(setSubGoalType('', clientId));
          dispatch(setGoalId(option.value, clientId));
        }
      }
    }
  };

  return {
    ...queryResult,
    data,
    actions: {
      handleOnChange,
    },
  };
};
